import { useState, useRef, useCallback, useEffect } from 'react';

interface Position {
  x: number;
  y: number;
}

interface UseDraggableOptions {
  initialPosition?: Position;
  bounds?: 'viewport' | 'parent' | DOMRect;
  onDragStart?: () => void;
  onDragEnd?: (position: Position) => void;
  handle?: string; // CSS selector for drag handle
}

export const useDraggable = (options: UseDraggableOptions = {}) => {
  const [position, setPosition] = useState<Position>(
    options.initialPosition || { x: 0, y: 0 }
  );
  const [isDragging, setIsDragging] = useState(false);
  
  const elementRef = useRef<HTMLDivElement>(null);
  const dragStartPos = useRef<Position | null>(null);
  const elementStartPos = useRef<Position | null>(null);

  const constrainToBounds = useCallback((pos: Position): Position => {
    if (!options.bounds || !elementRef.current) return pos;

    const rect = elementRef.current.getBoundingClientRect();
    
    if (options.bounds === 'viewport') {
      // Ensure at least 20% space from bottom
      const bottomLimit = window.innerHeight * 0.8 - rect.height;
      
      return {
        x: Math.max(0, Math.min(window.innerWidth - rect.width, pos.x)),
        y: Math.max(0, Math.min(bottomLimit, pos.y))
      };
    }
    
    if (options.bounds === 'parent') {
      const parent = elementRef.current.parentElement;
      if (!parent) return pos;
      
      const parentRect = parent.getBoundingClientRect();
      return {
        x: Math.max(0, Math.min(parentRect.width - rect.width, pos.x)),
        y: Math.max(0, Math.min(parentRect.height - rect.height, pos.y))
      };
    }
    
    // Custom bounds (DOMRect)
    return {
      x: Math.max(options.bounds.left, Math.min(options.bounds.right - rect.width, pos.x)),
      y: Math.max(options.bounds.top, Math.min(options.bounds.bottom - rect.height, pos.y))
    };
  }, [options.bounds]);

  const handlePointerDown = useCallback((e: PointerEvent) => {
    // Check if we should handle this event
    if (options.handle) {
      const target = e.target as HTMLElement;
      if (!target.matches(options.handle) && !target.closest(options.handle)) {
        return;
      }
    }

    e.preventDefault();
    
    const element = elementRef.current;
    if (!element) return;

    // Capture pointer
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    
    const rect = element.getBoundingClientRect();
    dragStartPos.current = { x: e.clientX, y: e.clientY };
    elementStartPos.current = { x: rect.left, y: rect.top };
    
    setIsDragging(true);
    options.onDragStart?.();
  }, [options.handle, options.onDragStart]);

  const handlePointerMove = useCallback((e: PointerEvent) => {
    if (!isDragging || !dragStartPos.current || !elementStartPos.current) return;
    
    e.preventDefault();
    
    const deltaX = e.clientX - dragStartPos.current.x;
    const deltaY = e.clientY - dragStartPos.current.y;
    
    const newPosition = {
      x: elementStartPos.current.x + deltaX,
      y: elementStartPos.current.y + deltaY
    };
    
    const constrainedPosition = constrainToBounds(newPosition);
    setPosition(constrainedPosition);
  }, [isDragging, constrainToBounds]);

  const handlePointerUp = useCallback((e: PointerEvent) => {
    if (!isDragging) return;
    
    // Release pointer
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    
    setIsDragging(false);
    dragStartPos.current = null;
    elementStartPos.current = null;
    
    options.onDragEnd?.(position);
  }, [isDragging, position, options.onDragEnd]);

  // Set up event listeners
  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const handleEl = options.handle ? element.querySelector(options.handle) : element;
    if (!handleEl) return;

    handleEl.addEventListener('pointerdown', handlePointerDown as any);
    
    if (isDragging) {
      document.addEventListener('pointermove', handlePointerMove as any);
      document.addEventListener('pointerup', handlePointerUp as any);
      document.addEventListener('pointercancel', handlePointerUp as any);
    }

    return () => {
      handleEl.removeEventListener('pointerdown', handlePointerDown as any);
      document.removeEventListener('pointermove', handlePointerMove as any);
      document.removeEventListener('pointerup', handlePointerUp as any);
      document.removeEventListener('pointercancel', handlePointerUp as any);
    };
  }, [handlePointerDown, handlePointerMove, handlePointerUp, isDragging, options.handle]);

  return {
    ref: elementRef,
    position,
    isDragging,
    setPosition,
    style: {
      position: 'fixed' as const,
      left: `${position.x}px`,
      top: `${position.y}px`,
      cursor: isDragging ? 'grabbing' : 'grab',
      touchAction: 'none' as const,
      userSelect: 'none' as const
    }
  };
};